
import dayjs from 'dayjs'
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { setAuthorizationToken, setTimezoneHeader } from '@/apis/http.api'
import { portalApi } from '@/apis/portal.api'
import { webSiteApi } from '@/apis/web-site.api'
import router, { menuRoutes, menuRouteEnum } from '@/router'
import { useFetchAndStoreAsync } from '@/composables/store'
import { useImageThumbnail } from '@/composables/image-thumbnail'
import i18n, { getLocalizedItems, getTranslatedItems, getTranslatedField } from '@/plugins/i18n'
import { stringToColor, textColor } from '@/lib/utils'

import { permissionFlagEnum, systemRoleEnum, usePortalStore, serviceTypeEnum } from './portal.store'
import { useAccountingStore } from './accounting.store'
import { usePlanningStore } from './planning.store'
import { calendarViewEnum } from './schedule.store'
import { accountingApi } from '@/apis/accounting.api'
import { useDetectDevice } from '@/composables/device'
import { listingItemRoute, navigateToInvoice, navigateToWarehouseMovements } from '@/composables/navigator'

export const initWebSiteStoreAsync = async() => {}

export const elementLayoutTypeEnum = Object.freeze({
  GRID: 0,
  DASHBOARD: 1,
  ROI_COMPARISON: 2,
})

export const searchResultTypeEnum = Object.freeze({
  EQUIPMENT: 'equipment',
  STAFF: 'staff',
  GROUP: 'group',
  PROJECT: 'project',
  QUOTE: 'quote',
  COMPANY: 'company',
  CONTACT: 'contact',
  RATE_CARD: 'rate_card',
  WAREHOUSE_MOVEMENT: 'warehouse_movement',
  INVOICE: 'invoice',
  EXPENSE: 'expense',
  // Frontend-only: sidebar leaf entries, matched client-side (labels are i18n keys)
  MENU: 'menu',
})

const searchResultTypeConfig = Object.freeze({
  [searchResultTypeEnum.EQUIPMENT]: { labelKey: 'common.equipment', icon: 'mdi-camcorder', color: 'teal' },
  [searchResultTypeEnum.STAFF]: { labelKey: 'common.staff', icon: 'mdi-account', color: 'blue' },
  [searchResultTypeEnum.GROUP]: { labelKey: 'common.group', icon: 'mdi-select-group', color: 'purple' },
  [searchResultTypeEnum.PROJECT]: { labelKey: 'common.project', icon: 'mdi-briefcase-outline', color: 'orange' },
  [searchResultTypeEnum.QUOTE]: { labelKey: 'common.quote', icon: 'mdi-file-document-outline', color: 'green' },
  [searchResultTypeEnum.COMPANY]: { labelKey: 'common.company', icon: 'mdi-office-building-outline', color: 'indigo' },
  [searchResultTypeEnum.CONTACT]: { labelKey: 'common.contact', icon: 'mdi-account-tie-outline', color: 'cyan' },
  [searchResultTypeEnum.INVOICE]: { labelKey: 'common.invoice', icon: 'mdi-receipt-text-outline', color: 'deep-orange' },
  [searchResultTypeEnum.EXPENSE]: { labelKey: 'common.expense', icon: 'mdi-receipt', color: 'amber' },
  [searchResultTypeEnum.RATE_CARD]: { labelKey: 'common.rateCard', icon: 'mdi-tag-multiple-outline', color: 'amber-8' },
  [searchResultTypeEnum.WAREHOUSE_MOVEMENT]: { labelKey: 'common.warehouseMovementsAlt', icon: 'mdi-dolly', color: 'brown' },
  [searchResultTypeEnum.MENU]: { labelKey: 'common.section', icon: 'mdi-page-next-outline', color: 'blue-grey' },
})

const availabilitySortFieldEnum = Object.freeze({
  AVAILABILITY: 'availability',
  NAME: 'name',
  FIRST_NAME: 'first_name',
  CONDITION: 'condition_value',
})

const availabilitySortFields = {
  [serviceTypeEnum.EQUIPMENT]: [
    { field: availabilitySortFieldEnum.AVAILABILITY, caption: 'common.availability', sort: 'desc' },
    { field: availabilitySortFieldEnum.NAME, caption: 'common.name', sort: null },
    { field: availabilitySortFieldEnum.CONDITION, caption: 'common.condition', sort: null },
  ],
  [serviceTypeEnum.STAFF]: [
    { field: availabilitySortFieldEnum.AVAILABILITY, caption: 'common.availability', sort: 'desc' },
    { field: availabilitySortFieldEnum.NAME, caption: 'common.lastName', sort: null },
    { field: availabilitySortFieldEnum.FIRST_NAME, caption: 'common.firstName', sort: null },
  ],
}

const REMEMBERED_LOGO_HEIGHT = 112
const REMEMBERED_AVATAR_HEIGHT = 192

export const staffDetailAllocationsFilterKey = 'staff-detail-allocations'

const projectSortFields = [
  { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
  { field: 'start', caption: 'common.startDate', sort: 'desc' },
  { field: 'end', caption: 'common.endDate', sort: null },
  { field: 'name', caption: 'common.name', sort: null },
  { field: 'status', caption: 'common.status', sort: null },
]

const defaultFilterSortFields = {
  listings: {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
      { field: 'company', caption: 'common.company', sort: 'asc' },
      { field: 'name', caption: 'common.name', sort: null },
    ]
  },
  projects: {
    sortMode: false,
    filterValue: null,
    sortFields: projectSortFields,
  },
  timeline: {
    sortMode: false,
    filterValue: null,
    sortFields: projectSortFields,
  },
  equipments: {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
      { field: 'name', caption: 'common.name', sort: 'asc' },
      { field: 'condition_value', caption: 'common.condition', sort: null },
      { field: 'status', caption: 'common.status', sort: null },
    ]
  },
  staffs: {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
      { field: 'name', caption: 'common.lastName', sort: 'asc' },
      { field: 'first_name', caption: 'common.firstName', sort: null },
    ]
  },
  'staff-movements': {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'start', caption: 'common.date', sort: 'desc' },
      { field: 'name', caption: 'common.name', sort: null },
    ]
  },
  [staffDetailAllocationsFilterKey]: {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'start', caption: 'common.date', sort: 'desc' },
      { field: 'name', caption: 'common.name', sort: null },
    ]
  },
  groups: {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
      { field: 'name', caption: 'common.name', sort: 'asc' },
      { field: 'code', caption: 'common.code', sort: null },
      { field: 'structure_type', caption: 'common.typology', sort: null },
    ]
  },
  companies: {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
      { field: 'name', caption: 'common.name', sort: 'asc' },
      { field: 'is_client', caption: 'common.client', sort: null },
    ]
  },
  'warehouse-stock': {
    sortMode: false,
    filterValue: null,
    sortFields: availabilitySortFields[serviceTypeEnum.EQUIPMENT],
  },
  'warehouse-list': {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'date', caption: 'common.date', sort: 'desc' },
      { field: 'name', caption: 'common.name', sort: null },
      { field: 'warehouse', caption: 'common.warehouse', sort: null },
    ]
  },
  'accounting-budget-documents': {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'date', caption: 'common.date', sort: 'desc' },
      { field: 'name', caption: 'common.name', sort: null },
      { field: 'listing_name', caption: 'common.listing', sort: null },
      { field: 'company_name', caption: 'common.company', sort: null },
    ]
  },
  'accounting-reports': {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
      { field: 'date_from', caption: 'common.date', sort: 'desc' },
      { field: 'name', caption: 'common.name', sort: null },
    ]
  },
  'accounting-expense-claims': {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
      { field: 'date_from', caption: 'common.date', sort: 'desc' },
      { field: 'number', caption: 'common.number', sort: null },
      { field: 'staff_label', caption: 'common.staff', sort: null },
    ]
  },
  'accounting-invoices': {
    sortMode: false,
    filterValue: null,
    sortFields: [
      { field: 'updated_at', caption: 'common.byUpdatedDate', sort: null },
      { field: 'date', caption: 'common.date', sort: 'desc' },
      { field: 'invoice_number', caption: 'common.invoiceNumber', sort: null },
      { field: 'company_name', caption: 'common.company', sort: null },
      { field: 'status', caption: 'common.status', sort: null },
    ]
  }
}

const cloneFilterSortDefaults = () => JSON.parse(JSON.stringify(defaultFilterSortFields))

const mergeFilterSortDefaults = (persisted) => {
  const defaults = cloneFilterSortDefaults()
  const merged = { ...defaults, ...persisted }
  Object.keys(defaults).forEach(routeName => {
    const sortFields = merged[routeName]?.sortFields
    if (!sortFields) {
      return
    }
    const existing = new Set(sortFields.map(f => f.field))
    defaults[routeName].sortFields.forEach((field, index) => {
      if (!existing.has(field.field)) {
        sortFields.splice(index, 0, { ...field })
      }
    })
  })
  return merged
}

export const useWebSiteStore = defineStore('web_site', () => {
  const api = webSiteApi
  const portalStore = usePortalStore()

  const accessToken = ref(null)
  const collaborator = ref({ id: 1 })
  const currencies = ref([])

  const unitOfMeasureCodeEnum = Object.freeze({
    UNIT: 'UN',
  })

  const documentRowTypeEnum = {
    LISTING_ITEM: 0,
    EXTRA: 1,
    COMMENT: 2,
  }

  const editedProjectId = ref(null)
  const eventSchedulerDate = ref(null)
  const timelineFocusDate = ref(null)
  const eventSchedulerMobileView = ref(calendarViewEnum.SCHEDULE)
  const eventSchedulerVacation = ref(null)
  const eventSchedulerView = ref(calendarViewEnum.WEEK)
  const eventCalendarSidebarOpen = ref(true)
  const fabActions = ref([])
  const pageTabOptions = ref([])
  const currentPageTab = ref(null)
  const onPageTabChange = ref(null)
  const breadcrumbSuffix = ref('')
  const roiComparisonShowDelta = ref(true)
  const roiComparisonDifferencesOnly = ref(false)
  const roiComparisonShowMine = ref(true)
  const roiComparisonShowShared = ref(true)
  const filterSortFields = ref(cloneFilterSortDefaults())
  const availabilitySortFieldsByType = ref(JSON.parse(JSON.stringify(availabilitySortFields)))

  const setAvailabilitySortFields = (serviceType) => {
    filterSortFields.value['warehouse-stock'].sortFields = availabilitySortFieldsByType.value[serviceType]
  }

  const buildSortOrderBy = (routeName) =>
    (filterSortFields.value[routeName]?.sortFields ?? [])
      .filter(x => x.sort !== null)
      .map(x => ({ getter: x.field, desc: x.sort === 'desc' }))

  const activateUpdatedDateSort = (routeName) => {
    const field = filterSortFields.value[routeName]?.sortFields.find(f => f.field === 'updated_at')
    if (!field) {
      return
    }
    field.sort = 'desc'
    filterSortFields.value[routeName].sortFields
      .filter(f => f.sort !== null)
      .forEach((f, index) => { f.order = index })
  }
  const isFilterBarVisible = ref(false)
  const lastUpdates = ref(null)
  const lastUpdatesTimestamp = ref(null)
  const lastUpdateDictionary = ref({})
  const menuEntries = ref([])
  const notificationsCount = ref(0)
  const notificationsLatest = ref(null)
  const notificationsArrivedAt = ref(null)
  const notificationsChangedAt = ref(null)
  const showEventVacations = ref(true)
  const showEventWarnings = ref(true)
  const countries = ref([])
  const unitsOfMeasure = ref([])
  const unitOfMeasureConversions = ref([])
  const vatTypes = ref([])
  const user = ref(null)
  const users = ref([])
  const rememberedLogin = ref(null)

  const userTypes = {
    NONE: 0,
    ADMIN: 1,
    COLLABORATOR: 2,
    PLANNER: 4
  }

  const schedulerViews = {
    PROJECTS: 0,
    STAFF: 1,
    EQUIPMENT: 2,
  }

  const gridEditItemId = ref(null)
  const gridStates = ref({})
  const gridStatesVersion = ref(null)
  const staffDetailHeaderCompact = ref(false)
  const equipmentDetailHeaderCompact = ref(false)
  const groupDetailHeaderCompact = ref(false)
  const companyDetailHeaderCompact = ref(false)
  const contactDetailHeaderCompact = ref(false)
  const availabilityMatrixNamesCollapsed = ref(false)
  const availabilityMatrixPeriod = ref(null)
  const staffMovementsOnlyAbsences = ref(false)
  const expenseClaimsUnclaimedByStaff = ref(true)
  const invoicesStatusFilter = ref(null)
  const reportsSubjectFilter = ref(null)
  const notificationsUnreadOnly = ref(false)
  const sideBarPinned = ref(false)
  const sideBarExpanded = ref(false)
  const signalBarHiddenPages = ref([])
  const kpiBarVisibleByView = ref({})
  const isKpiBarVisible = (viewKey) => kpiBarVisibleByView.value[viewKey] ?? false
  const toggleKpiBar = (viewKey) => {
    kpiBarVisibleByView.value = { ...kpiBarVisibleByView.value, [viewKey]: !isKpiBarVisible(viewKey) }
  }
  const collapsedInfoBoxes = ref({})
  const isInfoBoxCollapsed = (infoBoxId) => collapsedInfoBoxes.value[infoBoxId] ?? false
  const toggleInfoBox = (infoBoxId) => {
    collapsedInfoBoxes.value = { ...collapsedInfoBoxes.value, [infoBoxId]: !isInfoBoxCollapsed(infoBoxId) }
  }
  const showSearch = ref(false)
  const searchResultDetail = ref(null)
  const { isMobile } = useDetectDevice()

  const searchResultCodeRoutes = {
    [searchResultTypeEnum.EQUIPMENT]: menuRouteEnum.EQUIPMENTS,
    [searchResultTypeEnum.STAFF]: menuRouteEnum.STAFFS,
    [searchResultTypeEnum.GROUP]: menuRouteEnum.GROUPS,
    [searchResultTypeEnum.COMPANY]: menuRouteEnum.COMPANIES,
    [searchResultTypeEnum.QUOTE]: menuRouteEnum.ACCOUNTING_BUDGET_DOCUMENTS,
  }

  const searchResultLoaders = () => {
    const planningStore = usePlanningStore()
    return {
      [searchResultTypeEnum.EQUIPMENT]: (id) => planningStore.fetchEquipmentByIdAsync(id),
      [searchResultTypeEnum.STAFF]: (id) => planningStore.fetchStaffByIdAsync(id),
      [searchResultTypeEnum.GROUP]: (id) => planningStore.fetchGroupByIdAsync(id),
      [searchResultTypeEnum.PROJECT]: (id) => planningStore.fetchProjectByIdAsync(id),
      [searchResultTypeEnum.COMPANY]: (id) => planningStore.fetchCompanyByIdAsync(id),
      [searchResultTypeEnum.CONTACT]: (id) => planningStore.fetchContactByIdAsync(id),
      [searchResultTypeEnum.QUOTE]: (id) => accountingApi.getAsync(`${accountingApi.budgetDocumentsUrl}${id}/`),
    }
  }

  const loadSearchResultAsync = async(type, id) => (await searchResultLoaders()[type](id)).data

  // A result opened from outside the search (e.g. an AI chat link) carries only the id; the desktop pages filter by code.
  const searchResultDesktopTargetAsync = async(result) => {
    const codeRoute = searchResultCodeRoutes[result.type]
    if (codeRoute) {
      const code = result.code ?? (await loadSearchResultAsync(result.type, result.id)).code
      return { path: menuRoutes[codeRoute], query: { code } }
    }
    if (result.type === searchResultTypeEnum.PROJECT) {
      return { path: menuRoutes[menuRouteEnum.PROJECTS], query: { projectId: result.id } }
    }
    if (result.type === searchResultTypeEnum.RATE_CARD) {
      return listingItemRoute(result.id, result.item_code)
    }
    return null
  }

  const openSearchResultDetailAsync = async(result) => {
    if (result.type === searchResultTypeEnum.QUOTE) {
      searchResultDetail.value = { type: result.type, item: result.id }
      return
    }
    if (result.type === searchResultTypeEnum.RATE_CARD) {
      const planningStore = usePlanningStore()
      searchResultDetail.value = result.item_id
        ? { type: result.type, isItem: true, item: (await planningStore.fetchListingItemByIdAsync(result.item_id)).data }
        : { type: result.type, item: (await planningStore.fetchListingByIdAsync(result.id)).data }
      return
    }
    searchResultDetail.value = { type: result.type, item: await loadSearchResultAsync(result.type, result.id) }
  }

  const openSearchResultAsync = async(result) => {
    if (result.type === searchResultTypeEnum.CONTACT) {
      const contact = await loadSearchResultAsync(result.type, result.id)
      return openSearchResultAsync({ type: searchResultTypeEnum.COMPANY, id: contact.company })
    }
    if (result.type === searchResultTypeEnum.MENU) return router.push(result.route)
    if (result.type === searchResultTypeEnum.WAREHOUSE_MOVEMENT) {
      return navigateToWarehouseMovements(isMobile.value ? {} : { warehouse: result.warehouse_id, service: result.id })
    }
    if (result.type === searchResultTypeEnum.INVOICE) return navigateToInvoice(result.title)
    const target = isMobile.value ? null : await searchResultDesktopTargetAsync(result)
    return target ? router.push(target) : openSearchResultDetailAsync(result)
  }

  const closeSearchResultDetail = () => { searchResultDetail.value = null }

  const gridEntity = ref(null)
  const setGridEntity = (data) => {
    gridEntity.value = data?.id ? { routeName: router.currentRoute.value.name, id: data.id, label: data.name } : null
  }
  const chatPanelWidth = ref(28)
  const chatPanelDocked = ref(true)
  const chatConversationId = ref(null)
  const chatWindowBounds = ref({ left: 4, top: 6, width: 28, height: 36 })
  const showTasks = ref(false)
  const topBarHidden = ref(false)
  const taskPanelWidth = ref(26)
  const taskPanelDocked = ref(true)
  const taskWindowBounds = ref({ left: 34, top: 6, width: 26, height: 36 })
  const taskPanelGroupByType = ref(true)
  const taskPanelShowCompleted = ref(true)
  const taskPanelCollapsedGroups = ref([])
  const taskPanelShowMine = ref(false)
  const showNotifications = ref(false)
  const notificationPanelWidth = ref(26)
  const notificationPanelDocked = ref(true)
  const notificationWindowBounds = ref({ left: 62, top: 6, width: 26, height: 36 })
  const notificationPanelUnreadOnly = ref(false)
  const projectTasksShowMine = ref(false)
  const projectTimelineTaskListWidth = ref(36)
  const projectTimelineZoomIndex = ref(5)
  const projectTimelineProjectsCollapsed = ref(false)
  const projectTimelineTaskListCompact = ref(isMobile.value)
  const warehouseAvailabilityTaskListWidth = ref(22)

  const _keydownHandlers = new Set()
  const addKeydownHandler = (fn) => _keydownHandlers.add(fn)
  const removeKeydownHandler = (fn) => _keydownHandlers.delete(fn)
  const dispatchKeydown = (event) => _keydownHandlers.forEach(fn => fn(event))

  const getMenuIcon = (menuRoute) => menuEntries.value.find(menuEntry => menuEntry.code === menuRoute)?.icon

  const getMenuEntriesWithRoute = (all = false) => {
    return menuEntries.value.filter(menuEntry => all || menuEntry.visible).map(menuEntry => {
      const route = menuRoutes[menuEntry.code]
      if (route) {
        menuEntry.route = route
      }
      return menuEntry
    })
  }

  const getRememberedLogoAsync = () => {
    const logoUrl = useAccountingStore().getCompanyLogoUrl()
    if (!logoUrl) return null
    const { toThumbnailDataUrlAsync } = useImageThumbnail()
    return toThumbnailDataUrlAsync(logoUrl, REMEMBERED_LOGO_HEIGHT).catch((err) => {
      console.error('Failed to cache company logo', err)
      return null
    })
  }

  const getRememberedAvatarAsync = () => {
    if (!user.value.avatar) return null
    const { toThumbnailDataUrlAsync } = useImageThumbnail()
    return toThumbnailDataUrlAsync(`data:image/png;base64,${user.value.avatar}`, REMEMBERED_AVATAR_HEIGHT).catch((err) => {
      console.error('Failed to cache user avatar', err)
      return null
    })
  }

  const rememberLoginAsync = async() => {
    const accountingStore = useAccountingStore()
    if (!accountingStore.companyData?.id) await accountingStore.fetchCompanyDataAsync(null, true)
    const { companyData } = accountingStore
    rememberedLogin.value = {
      companyName: companyData.name,
      firstName: user.value.first_name,
      username: user.value.username,
      language: user.value.preferred_language,
      logo: await getRememberedLogoAsync(),
      avatar: await getRememberedAvatarAsync(),
      initials: getUserInitials(),
    }
  }

  const forgetRememberedLogin = () => {
    rememberedLogin.value = null
  }

  const loginAsync = async(username, password, rememberDevice = true) => {
    if (!username || !password) {
      return logoutAsync()
    }
    const res = await api.getTokenAsync(username, password)
    await setTokenAsync(res.data)
    await fetchMenuEntriesAsync({all: true}, true)
    await routeToHomeAsync()
    if (rememberDevice) {
      rememberLoginAsync().catch((err) => console.error('Failed to remember login', err))
    } else {
      forgetRememberedLogin()
    }
  }

  const autoLoginAsync = async() => {
    await refreshTokenAsync()
    await fetchMenuEntriesAsync({all: true}, true)
  }

  const routeToHomeAsync = async() => {
    if (accessToken.value) {
      return router.replace(menuRoutes[menuRouteEnum.HOME])
    } else {
      return router.push(menuRoutes[menuRouteEnum.LOGIN])
    }
  }

  const logoutAsync = async() => {
    await api.logoutAsync()
    $reset()
    return router.push(menuRoutes[menuRouteEnum.LOGIN])
  }

  const isAuthenticated = () => accessToken.value !== null

  const refreshTokenAsync = async(config) => {
    const res = await api.getRefreshTokenAsync(config)
    return setTokenAsync(res.data)
  }

  const setTokenAsync = async(token) => {
    accessToken.value = token.access
    setAuthorizationToken(accessToken.value)
    setTimezoneHeader()

    const jwtBase64 = accessToken.value.split('.')[1]
    const jwtToken = JSON.parse(atob(jwtBase64))
    await setUserAsync(jwtToken.user_id)
  }

  const setUserAsync = async(userId) => {
    const res = await api.getUserDetailsAsync(userId)
    if (!res.data) {
      return logoutAsync()
    }
    await portalStore.fetchLanguagesAsync()
    await portalStore.setLanguageAsync(res.data.preferred_language)
    user.value = res.data
    await fetchRolesAsync()
  }

  const setGridState = (gridName, gridState) => { gridStates.value[gridName] = gridState }

  const updateOwnUserDetailsAsync = async(data) => {
    const res = await api.updateUserDetailsAsync(user.value.id, data)
    user.value = res.data
    if (rememberedLogin.value) await rememberLoginAsync()
  }

  const navigateToMyProfile = () => {
    const accountingStore = useAccountingStore()
    return router.push({ path: menuRoutes[menuRouteEnum.COMPANY], query: { tab: accountingStore.companyDataTabEnum.PROFILE } })
  }

  const saveDashboardLayoutAsync = async(layout) => {
    await api.saveDashboardLayoutAsync(user.value.id, layout)
    user.value.dashboard_layout = layout
  }

  const getGridStateVersion = () => window.config?.GRID_STATE_VERSION ?? null

  // App-level grid layout changes (added/reordered columns) are otherwise masked by each user's
  // saved state: bumping GRID_STATE_VERSION in config.js wipes every stored grid state once.
  const reconcileGridStateVersion = () => {
    const current = getGridStateVersion()
    if (gridStatesVersion.value !== current) {
      gridStates.value = {}
      gridStatesVersion.value = current
    }
  }

  const fetchLastUpdateAsync = async(force = false) => {
    await useFetchAndStoreAsync(lastUpdates, () => api.getLastUpdateAsync(), force)
    const portalModels = lastUpdates.value.filter(x => x.model.startsWith('portal') && lastUpdates.value.filter(x => !x.model.startsWith('portal')).some(y => y.model.split('.')[2] === x.model.split('.')[2]))
    lastUpdates.value = lastUpdates.value
      .map(x => {
        x.updated_at = dayjs(x.updated_at).toISOString()
        return x
      })
      .filter(x => !portalModels.some(y => y.model === x.model)).map(x => {
        const currentModelName = x.model.split('.')[2]
        const portalModel = portalModels.find(x => x.model === `portal.models.${currentModelName}`)
        x.updated_at = dayjs(x.updated_at || 0).isAfter(portalModel?.updated_at || 0) ? x.updated_at : portalModel?.updated_at
        return x
      })
    return lastUpdates
  }

  const activateUserAsync = async(uid, token, password, isDryRun = false) => api.activateUserAsync(uid, token, password, isDryRun)
  const fetchCurrenciesAsync = async(params, store = false) => useFetchAndStoreAsync(currencies, () => api.getCurrenciesAsync(params), store)
  const updateCurrencyAsync = async(id, data) => api.patchAsync(`${api.currenciesUrl}${id}/`, data)
  const fetchMenuEntriesAsync = async(params, store = false) => useFetchAndStoreAsync(menuEntries, () => api.getMenuEntries(params), store)
  const applyNotificationsState = ({ count, latest }) => {
    notificationsCount.value = count
    notificationsLatest.value = latest
  }
  const fetchNotificationsUnreadCountAsync = async() => applyNotificationsState((await api.getNotificationsUnreadCountAsync()).data)
  const pollNotificationsUnreadCountAsync = async() => {
    const previousLatest = notificationsLatest.value
    await fetchNotificationsUnreadCountAsync()
    const arrived = !!notificationsLatest.value && (!previousLatest || dayjs(notificationsLatest.value).isAfter(previousLatest))
    if (arrived) notificationsArrivedAt.value = Date.now()
    return arrived
  }
  const fetchUnitsOfMeasureAsync = async(params, store = false) => useFetchAndStoreAsync(unitsOfMeasure, () => api.getUnitsOfMeasureAsync(params), store)
  const fetchUnitOfMeasureConversionsAsync = async(params, store = false) => useFetchAndStoreAsync(unitOfMeasureConversions, () => api.getUnitOfMeasureConversionsAsync(params), store)
  const fetchCountriesAsync = async(params, store = false) => useFetchAndStoreAsync(countries, () => portalApi.getCountriesAsync(params), store)
  const fetchVatTypesAsync = async(params, store = false) => useFetchAndStoreAsync(vatTypes, () => api.getVatTypesAsync(params), store)
  const createVatTypeAsync = async(data) => api.postAsync(api.vatTypesUrl, data)
  const deleteVatTypeAsync = async(id, config) => api.deleteAsync(`${api.vatTypesUrl}${id}/`, config)
  const fetchUsersAsync = async(params, store = false) => useFetchAndStoreAsync(users, () => api.getUsersAsync(params), store)

  const fetchNextElementLayoutIdAsync = async() => (await api.getNextIdAsync(api.elementLayoutsUrl)).data.next_id
  const fetchElementLayoutsAsync = async(elementType, elementKey = '') => (await api.getAsync(api.elementLayoutsUrl, { element_type: elementType, element_key: elementKey })).data
  const fetchDefaultElementLayoutAsync = async(elementType, elementKey = '') => (await api.getDefaultElementLayoutAsync({ element_type: elementType, element_key: elementKey })).data
  const createElementLayoutAsync = async(data) => api.postAsync(api.elementLayoutsUrl, data)
  const upsertElementLayoutAsync = async(data) => api.upsertElementLayoutAsync(data)
  const updateElementLayoutAsync = async(id, payload) => api.patchAsync(`${api.elementLayoutsUrl}${id}/`, payload)
  const deleteElementLayoutAsync = async(id) => api.deleteAsync(`${api.elementLayoutsUrl}${id}/`)
  const toggleElementLayoutSharedAsync = async(id) => api.toggleElementLayoutSharedAsync(id)
  const setDefaultElementLayoutAsync = async(id) => api.setDefaultElementLayoutAsync(id)
  const setDefaultBuiltinElementLayoutAsync = async(elementType, elementKey, builtin) => api.setDefaultBuiltinElementLayoutAsync({ element_type: elementType, element_key: elementKey, builtin })
  const clearDefaultElementLayoutAsync = async(elementType, elementKey) => api.clearDefaultElementLayoutAsync({ element_type: elementType, element_key: elementKey })

  const getLocalizedDocumentRowTypes = () => getLocalizedItems(Object.keys(documentRowTypeEnum).map(i => ({id: documentRowTypeEnum[i], name: `common.documentRowTypes.${i.toLowerCase()}`})))
  const getLocalizedCountries = () => getTranslatedItems(countries.value)
  const getLocalizedCurrencies = () => getTranslatedItems(currencies.value)
  const getLocalizedUnitsOfMeasure = () => (unitsOfMeasure.value || []).map(u => ({
    ...u,
    name: getTranslatedField(u, 'name'),
    symbol: getTranslatedField(u, 'symbol'),
  }))
  const vatPercentageDisplay = (percentage) => Number((Number(percentage) * 100).toFixed(6))
  const getLocalizedVatTypes = () => (vatTypes.value || []).map(vat => ({
    ...vat,
    name: `${vatPercentageDisplay(vat.percentage)}%`,
  }))

  const $reset = () => {
    setAuthorizationToken(null)
    setTimezoneHeader(null)

    accessToken.value = null
    fabActions.value = []
    gridStates.value = {}
    filterSortFields.value = cloneFilterSortDefaults()
    lastUpdates.value = null
    lastUpdatesTimestamp.value = null
    lastUpdateDictionary.value = {}
    menuEntries.value = []
    notificationsCount.value = 0
    notificationsLatest.value = null
    user.value = null
    users.value = []
    unitsOfMeasure.value = []
    unitOfMeasureConversions.value = []
    vatTypes.value = []

    const portalStore = usePortalStore()
    portalStore.$reset()
  }

  const getDefaultCurrency = () => {
    const accountingStore = useAccountingStore()
    return currencies.value.find(x => x.id === accountingStore.companyData.default_currency_ref)
  }
  const getDefaultVatType = () => {
    const accountingStore = useAccountingStore()
    return vatTypes.value.find(x => x.id === accountingStore.companyData.default_vat_type_ref)
  }
  const getDefaultStartTime = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_start_time
  }
  const getDefaultEndTime = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_end_time
  }
  const getDefaultDocumentRowType = () => documentRowTypeEnum.LISTING_ITEM
  // Expense (extra) rows have their own company default, falling back to plain units.
  const getDefaultExtraUnitOfMeasure = () => {
    const accountingStore = useAccountingStore()
    return unitsOfMeasure.value.find(x => x.id === accountingStore.companyData.default_extra_unit_of_measure_ref) ||
      unitsOfMeasure.value.find(x => x.code === unitOfMeasureCodeEnum.UNIT)
  }

  const getDefaultUnitOfMeasure = () => {
    const accountingStore = useAccountingStore()
    return unitsOfMeasure.value.find(x => x.id === accountingStore.companyData.default_unit_of_measure_ref) ||
      getDefaultExtraUnitOfMeasure()
  }
  const getDefaultQuotePrintTemplate = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_quote_print_template ?? null
  }
  const getDefaultPaymentDays = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_payment_days ?? 0
  }
  const getPaymentDaysOptions = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.payment_days_options ?? []
  }
  const getDefaultInvoicePrintTemplate = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_invoice_print_template ?? null
  }
  const getDefaultReportPrintTemplate = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_report_print_template ?? null
  }
  const getDefaultReportColumnTemplate = (reportType) => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData[accountingStore.reportColumnTemplateDefaultFields[reportType]] ?? null
  }
  const getDefaultWarehouse = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_warehouse ?? null
  }
  const getDefaultTransitWarehouse = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_transit_warehouse ?? null
  }
  const getDefaultOrdersWarehouse = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData.default_orders_warehouse ?? null
  }
  const getRestDays = () => useAccountingStore().companyData?.rest_days ?? []
  const isRestDay = (day) => getRestDays().includes(day.isoWeekday())

  const getDefaultTimelinePeriod = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData?.timeline_default_period ?? null
  }
  const getDefaultShowListingFirst = () => {
    const accountingStore = useAccountingStore()
    return accountingStore.companyData?.timeline_default_show_listing_first ?? false
  }
  const getInputDebounce = () => window.config.INPUT_DEBOUNCE
  const getNotificationsInterval = () => window.config.NOTIFICATIONS_INTERVAL
  const getChatHistoryPollInterval = () => window.config.CHAT_HISTORY_POLL_INTERVAL

  const searchAsync = async(q) => (await api.searchAsync(q)).data.results
  const searchMenuEntries = (query, mobile = false) => {
    const needle = (query ?? '').trim().toLowerCase()
    if (!needle) return []
    const entries = getMenuEntriesWithRoute(true).filter(entry => mobile ? entry.visible_mobile : entry.visible)
    const label = (entry) => i18n.global.t(entry.code)
    const isLeaf = (entry) => !entries.some(other => other.parent_id === entry.id)
    const breadcrumb = (entry) => {
      const chain = []
      let parent = entries.find(other => other.id === entry.parent_id)
      while (parent) {
        chain.unshift(label(parent))
        parent = entries.find(other => other.id === parent.parent_id)
      }
      return chain.join(' › ')
    }
    return entries
      .filter(entry => entry.route && isLeaf(entry) && label(entry).toLowerCase().includes(needle))
      .map(entry => ({
        type: searchResultTypeEnum.MENU,
        id: entry.id,
        title: label(entry),
        subtitle: breadcrumb(entry) || undefined,
        icon: entry.icon,
        route: entry.route,
      }))
  }
  const getSearchResultTypeConfig = (type) => {
    const config = searchResultTypeConfig[type]
    const base = config.color.replace(/-\d+$/, '')
    return { ...config, bgColor: `${base}-1`, textColor: `${base}-9` }
  }
  const getUserInitials = () => {
    if (!user.value) {
      return ''
    }
    if (user.value.first_name && user.value.last_name) {
      return (user.value.first_name[0] + user.value.last_name[0]).toUpperCase()
    }
    const email = user.value.username.split('@')
    if (email[0].includes('.')) {
      const tmp = email[0].split('.')
      return tmp[0][0].toUpperCase() + tmp[1][0].toUpperCase()
    }
    const initials = email[0][0].toUpperCase()
    if (email.length > 1) {
      return initials + email[1][0].toUpperCase()
    }
    return initials
  }
  const fetchPermissionMatrixAsync = async() => (await api.getAsync(api.permissionsUrl)).data

  const postPermissionsAsync = async(path, body) => {
    const { data } = await api.postAsync(`${api.permissionsUrl}${path}`, body)
    await refreshTokenAsync()
    return data
  }

  const updatePermissionsAsync = (changes) => postPermissionsAsync('bulk-update/', changes)

  const resetPermissionsAsync = () => postPermissionsAsync('reset/')

  const permissions = computed(() => getDecodedToken()?.permissions ?? 0)
  const can = (flag) => (permissions.value & flag) === flag
  const canAny = (flags) => (permissions.value & flags) !== 0
  const canReadListings = computed(() => canAny(permissionFlagEnum.RATE_CARDS_VIEW | permissionFlagEnum.PLANNING | permissionFlagEnum.PROJECTS |
    permissionFlagEnum.QUOTES_VIEW | permissionFlagEnum.REPORTS))
  const isOwnRecord = (record) => record?.created_by === user.value?.id
  const canManageOwned = (record, flag) => !!record && (can(flag) || isOwnRecord(record))
  const staffId = computed(() => user.value?.service_id ?? null)
  const hasStaff = computed(() => !!staffId.value)
  const canApproveExpenseClaims = computed(() => can(permissionFlagEnum.APPROVALS))

  const roles = ref([])
  const systemRoleColors = Object.freeze({
    [systemRoleEnum.ADMINISTRATOR]: 'blue',
    [systemRoleEnum.USER]: 'green',
    [systemRoleEnum.AI_ASSISTANT]: 'deep-purple',
  })
  const systemRoleKeys = Object.fromEntries(Object.entries(systemRoleEnum).map(([key, value]) => [value, key.toLowerCase()]))
  const getRoleLabel = (role) => (role.system_role === null ? role.name : i18n.global.t(`permissions.role.${systemRoleKeys[role.system_role]}`))
  const getLocalizedRole = (role) => {
    const label = getRoleLabel(role)
    const color = systemRoleColors[role.system_role] ?? stringToColor(label)
    return { ...role, name: label, label, color, textColor: textColor(color) }
  }
  const getLocalizedRoles = () => roles.value.map(getLocalizedRole)
  const fetchRolesAsync = async() => useFetchAndStoreAsync(roles, () => api.getAsync(api.rolesUrl), true)
  const createRoleAsync = (data) => api.postAsync(api.rolesUrl, data)
  const updateRoleAsync = (id, data) => api.patchAsync(`${api.rolesUrl}${id}/`, data)
  const deleteRoleAsync = (id) => api.deleteAsync(`${api.rolesUrl}${id}/`)

  const getDecodedToken = () => {
    if (!accessToken.value) {
      return null
    }
    const jwtBase64 = accessToken.value.split('.')[1]
    return JSON.parse(atob(jwtBase64))
  }

  const applyNotificationsChangeAsync = async(request) => {
    applyNotificationsState((await request).data)
    notificationsChangedAt.value = Date.now()
  }
  const setNotificationsReadAsync = (readState) => applyNotificationsChangeAsync(api.postAsync(api.notificationsMarkAsReadUrl, readState))
  const snoozeNotificationsAsync = (ids, until) => applyNotificationsChangeAsync(api.postAsync(api.notificationsSnoozeUrl, { ids, until }))
  const fetchNotificationSettingsAsync = async() => (await api.getAsync(api.notificationSettingsUrl)).data
  const saveNotificationSettingsAsync = async(settings) => (await api.putAsync(api.notificationSettingsUrl, settings)).data

  return {
    canApproveExpenseClaims,
    api,
    accessToken,
    collaborator,
    currencies,
    documentRowTypeEnum,
    unitOfMeasureCodeEnum,
    editedProjectId,
    eventSchedulerDate,
    timelineFocusDate,
    eventSchedulerMobileView,
    eventSchedulerVacation,
    eventSchedulerView,
    eventCalendarSidebarOpen,
    fabActions,
    pageTabOptions,
    currentPageTab,
    onPageTabChange,
    breadcrumbSuffix,
    roiComparisonShowDelta,
    roiComparisonDifferencesOnly,
    roiComparisonShowMine,
    roiComparisonShowShared,
    availabilitySortFieldEnum,
    setAvailabilitySortFields,
    buildSortOrderBy,
    activateUpdatedDateSort,
    filterSortFields,
    projectTimelineTaskListWidth,
    projectTimelineZoomIndex,
    projectTimelineProjectsCollapsed,
    projectTimelineTaskListCompact,
    warehouseAvailabilityTaskListWidth,
    addKeydownHandler,
    removeKeydownHandler,
    dispatchKeydown,
    gridEditItemId,
    gridStates,
    gridStatesVersion,
    staffDetailHeaderCompact,
    equipmentDetailHeaderCompact,
    groupDetailHeaderCompact,
    companyDetailHeaderCompact,
    contactDetailHeaderCompact,
    availabilityMatrixNamesCollapsed,
    availabilityMatrixPeriod,
    staffMovementsOnlyAbsences,
    expenseClaimsUnclaimedByStaff,
    invoicesStatusFilter,
    reportsSubjectFilter,
    notificationsUnreadOnly,
    getGridStateVersion,
    reconcileGridStateVersion,
    can,
    canAny,
    canReadListings,
    fetchPermissionMatrixAsync,
    updatePermissionsAsync,
    resetPermissionsAsync,
    permissionFlagEnum,
    isOwnRecord,
    canManageOwned,
    roles,
    getLocalizedRole,
    getLocalizedRoles,
    fetchRolesAsync,
    createRoleAsync,
    updateRoleAsync,
    deleteRoleAsync,
    isFilterBarVisible,
    lastUpdates,
    lastUpdatesTimestamp,
    lastUpdateDictionary,
    menuEntries,
    getMenuIcon,
    notificationsCount,
    notificationsArrivedAt,
    notificationsChangedAt,
    schedulerViews,
    showEventVacations,
    showEventWarnings,
    sideBarExpanded,
    sideBarPinned,
    signalBarHiddenPages,
    kpiBarVisibleByView,
    isKpiBarVisible,
    toggleKpiBar,
    collapsedInfoBoxes,
    isInfoBoxCollapsed,
    toggleInfoBox,
    showSearch,
    searchResultDetail,
    openSearchResultAsync,
    openSearchResultDetailAsync,
    closeSearchResultDetail,
    gridEntity,
    setGridEntity,
    chatPanelWidth,
    chatPanelDocked,
    chatConversationId,
    chatWindowBounds,
    showTasks,
    topBarHidden,
    taskPanelWidth,
    taskPanelDocked,
    taskWindowBounds,
    taskPanelGroupByType,
    taskPanelShowCompleted,
    taskPanelCollapsedGroups,
    taskPanelShowMine,
    showNotifications,
    notificationPanelWidth,
    notificationPanelDocked,
    notificationWindowBounds,
    notificationPanelUnreadOnly,
    projectTasksShowMine,
    user,
    users,
    userTypes,
    rememberedLogin,
    forgetRememberedLogin,
    countries,
    unitsOfMeasure,
    unitOfMeasureConversions,
    vatTypes,
    $reset,
    activateUserAsync,
    autoLoginAsync,
    fetchCountriesAsync,
    fetchCurrenciesAsync,
    updateCurrencyAsync,
    fetchLastUpdateAsync,
    fetchMenuEntriesAsync,
    fetchNotificationsUnreadCountAsync,
    pollNotificationsUnreadCountAsync,
    fetchUnitsOfMeasureAsync,
    fetchUnitOfMeasureConversionsAsync,
    fetchUsersAsync,
    fetchVatTypesAsync,
    createVatTypeAsync,
    deleteVatTypeAsync,
    fetchElementLayoutsAsync,
    fetchNextElementLayoutIdAsync,
    fetchDefaultElementLayoutAsync,
    createElementLayoutAsync,
    upsertElementLayoutAsync,
    updateElementLayoutAsync,
    deleteElementLayoutAsync,
    toggleElementLayoutSharedAsync,
    setDefaultElementLayoutAsync,
    setDefaultBuiltinElementLayoutAsync,
    clearDefaultElementLayoutAsync,
    getDecodedToken,
    getDefaultCurrency,
    getDefaultStartTime,
    getDefaultEndTime,
    getDefaultDocumentRowType,
    getDefaultVatType,
    getDefaultUnitOfMeasure,
    getDefaultExtraUnitOfMeasure,
    getDefaultWarehouse,
    getDefaultQuotePrintTemplate,
    getDefaultInvoicePrintTemplate,
    getDefaultReportPrintTemplate,
    getDefaultReportColumnTemplate,
    staffId,
    hasStaff,
    getDefaultPaymentDays,
    getPaymentDaysOptions,
    getDefaultTransitWarehouse,
    getDefaultOrdersWarehouse,
    getDefaultTimelinePeriod,
    getRestDays,
    isRestDay,
    getDefaultShowListingFirst,
    getInputDebounce,
    getNotificationsInterval,
    getChatHistoryPollInterval,
    searchAsync,
    searchMenuEntries,
    getSearchResultTypeConfig,
    getMenuEntriesWithRoute,
    getUserInitials,
    updateOwnUserDetailsAsync,
    navigateToMyProfile,
    getLocalizedCountries,
    getLocalizedCurrencies,
    getLocalizedDocumentRowTypes,
    getLocalizedUnitsOfMeasure,
    getLocalizedVatTypes,
    isAuthenticated,
    loginAsync,
    logoutAsync,
    refreshTokenAsync,
    routeToHomeAsync,
    setGridState,
    saveDashboardLayoutAsync,
    setNotificationsReadAsync,
    snoozeNotificationsAsync,
    fetchNotificationSettingsAsync,
    saveNotificationSettingsAsync,
  }
}, {
  persist: {
    enabled: true,
    strategies: [
      {
        afterRestore: (ctx) => {
          ctx.store.filterSortFields = mergeFilterSortDefaults(ctx.store.filterSortFields)
          ctx.store.reconcileGridStateVersion()
        },
        storage: localStorage, paths: [
          'editingMode',
          'eventCalendarSidebarOpen',
          'eventSchedulerMobileView',
          'eventSchedulerView',
          'equipment',
          'filterSortFields',
          'projectTimelineTaskListWidth',
          'projectTimelineZoomIndex',
          'projectTimelineProjectsCollapsed',
          'projectTimelineTaskListCompact',
          'warehouseAvailabilityTaskListWidth',
          'chatPanelWidth',
          'chatPanelDocked',
          'chatConversationId',
          'chatWindowBounds',
          'taskPanelWidth',
          'taskPanelDocked',
          'taskWindowBounds',
          'taskPanelGroupByType',
          'taskPanelShowCompleted',
          'taskPanelCollapsedGroups',
          'taskPanelShowMine',
          'notificationPanelWidth',
          'notificationPanelDocked',
          'notificationWindowBounds',
          'notificationPanelUnreadOnly',
          'projectTasksShowMine',
          'gridStates',
          'gridStatesVersion',
          'lastUpdateDictionary',
          'lastUpdates',
          'lastUpdatesTimestamp',
          'menuEntries',
          'projects',
          'showEventVacations',
          'showEventWarnings',
          'sideBarPinned',
          'signalBarHiddenPages',
          'kpiBarVisibleByView',
          'collapsedInfoBoxes',
          'availabilityMatrixNamesCollapsed',
          'availabilityMatrixPeriod',
          'staffMovementsOnlyAbsences',
          'expenseClaimsUnclaimedByStaff',
          'invoicesStatusFilter',
          'reportsSubjectFilter',
          'notificationsUnreadOnly',
          'equipmentDetailHeaderCompact',
          'groupDetailHeaderCompact',
          'staffDetailHeaderCompact',
          'companyDetailHeaderCompact',
          'roiComparisonDifferencesOnly',
          'roiComparisonShowDelta',
          'roiComparisonShowMine',
          'roiComparisonShowShared',
          'contactDetailHeaderCompact',
          'staffEvents',
          'unitsOfMeasure',
          'unitOfMeasureConversions',
          'vatTypes',
          'rememberedLogin',
        ]
      },
      { storage: sessionStorage, paths: ['gridEditItemId'] }
    ],
  },
})
